// Startup Journey Workflow Types

export type JourneyStage = 'Beginner' | 'Have Idea' | 'In Progress';

export type JourneyMarket = 'IT/Tech' | 'E-commerce' | 'Social Media/Influencer' | 'Other';

export type JourneyPersona = 'skilled-maker' | 'expert-service' | 'founder-plan';

export type StepStatus = 'locked' | 'current' | 'completed' | 'skipped';

export type ActionType = 'survey' | 'task' | 'team' | 'preRelease' | 'ai' | 'launchAssets' | 'instruction' | 'definitionOfDone';

export type CompletionMethod = 'manual' | 'automatic' | 'skipped';

export type JourneyActionType = 'reset' | 'customization' | 'skip_step';

export type SkipType = 'already_done' | 'not_applicable' | 'custom';

export type CustomizationType = 'step_order' | 'step_modification' | 'template_override';

export type CustomizationLevel = 'standard' | 'experienced' | 'expert';

// Optional detailed evaluation with 8 factors rated 0-3
export interface JourneyEvaluation {
  idea: number;        // 0-3: How developed is your idea/concept?
  skills: number;      // 0-3: How strong are your relevant skills/talent?
  team: number;        // 0-3: Do you have a team or co-founders?
  funding: number;     // 0-3: Do you have funding/capital available?
  market: number;      // 0-3: How well do you know your target market?
  tech: number;        // 0-3: How comfortable are you with technology/tools?
  network: number;     // 0-3: How strong is your professional network/connections?
  time: number;        // 0-3: How much time can you dedicate to this?
}

// Setup data collected from user
export interface JourneySetup {
  stage: JourneyStage;
  market: JourneyMarket;
  evaluation?: JourneyEvaluation;
}

// Action within a workflow step
export interface StepAction {
  text: string;           // Display text for the action button
  text_zh?: string;       // Chinese translation of action text
  description?: string;   // Action description
  description_zh?: string; // Chinese translation of description
  helpText?: string;      // Help text for the action
  helpText_zh?: string;   // Chinese translation of help text
  type: ActionType;       // Type of platform feature to integrate with
  templateId?: string;    // Optional ID of specific template, or will be resolved contextually
  pointsCost: number;     // Points required to perform this action
  pointsReward: number;   // Points awarded for completing this action
  priority?: 'critical' | 'important' | 'optional' | 'context';  // Priority level for UI display
  phase?: 'setup' | 'build' | 'deploy' | string;  // Phase grouping for multi-phase steps
}

// Individual step within a journey
export interface JourneyStep {
  id: string;
  journeyId: string;
  stepIndex: number;
  title: string;
  title_zh?: string;            // Chinese translation of title
  description: string;
  description_zh?: string;      // Chinese translation of description
  actions: StepAction[];
  dodCriteria: string[];        // Definition of Done criteria
  dodCriteria_zh?: string[];    // Chinese translation of DoD criteria
  timeEstimateWeeks: number;
  status: StepStatus;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  isCustomized?: boolean;       // Whether this step has been customized
  originalStepIndex?: number;   // Original position if reordered
  skipJustification?: string;   // Quick access to skip reason
  type?: string;                // Optional step type identifier
  completed?: boolean;          // Alternative completion flag
}

// Main journey record
export interface Journey {
  id: string;
  userId: string;
  stage: JourneyStage;
  market: JourneyMarket;
  persona: JourneyPersona;
  evaluation?: JourneyEvaluation;
  currentStepIndex: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  steps?: JourneyStep[];        // Optional populated steps
  resetCount?: number;          // Number of times this journey has been reset
  isCustomized?: boolean;       // Whether this journey has customizations
  customizationLevel?: CustomizationLevel; // User's customization access level
  title?: string;               // Optional journey title
  description?: string;         // Optional journey description
}

// Template for pre-filling platform features
export interface WorkflowTemplate {
  id: string;
  type: ActionType;
  title: string;
  conditions: string[];         // Condition strings for matching (e.g., "stage == 'Beginner'")
  content: any;                // Template-specific content (varies by type)
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Enhanced template interface with persona-specific conditions and priority
export interface EnhancedWorkflowTemplate extends WorkflowTemplate {
  personaConditions: string[];  // Persona-specific conditions (e.g., "persona == 'skilled-maker'")
  stepConditions: string[];     // Step-specific conditions (e.g., "step == 'idea-generation'")
  marketConditions: string[];   // Market-specific conditions (e.g., "market == 'digital-products'")
  priority: number;             // Template priority (higher = more preferred)
  aiGenerated?: boolean;        // Whether template was AI-generated
  generatedAt?: Date;           // When template was generated (for AI templates)
}

// Step completion tracking
export interface StepCompletion {
  id: string;
  stepId: string;
  userId: string;
  completionMethod: CompletionMethod;
  reflectionNotes?: string;
  pointsAwarded: number;
  createdAt: Date;
}

// Template content types for different action types
export interface SurveyQuestion {
  text: string;
  text_zh?: string;
  type: 'multiple-choice' | 'text';
  options: string[];
  options_zh?: string[];
}

export interface SurveyTemplateContent {
  title: string;
  title_zh?: string;
  description?: string;
  description_zh?: string;
  questions: (string | SurveyQuestion)[];  // Support both old string format and new structured format
  questions_zh?: string[];  // Kept for backward compatibility
  targetResponses?: number;
}

export interface TaskTemplateContent {
  title: string;
  description?: string;
  checklist: string[];
  expectedOutputs: string[];
  timeEstimate?: string;
}

export interface TeamTemplateContent {
  title: string;
  description?: string;
  roles: {
    title: string;
    description: string;
    skills: string[];
    timeCommitment?: string;
  }[];
}

export interface PreReleaseTemplateContent {
  title: string;
  description?: string;
  headline: string;
  features: string[];
  targetSignups?: number;
  launchGoals: string[];
}

// Union type for all template content types
export type TemplateContent = 
  | SurveyTemplateContent 
  | TaskTemplateContent 
  | TeamTemplateContent 
  | PreReleaseTemplateContent;

// Database row types (snake_case to match database schema)
export interface JourneyRow {
  id: string;
  user_id: string;
  stage: JourneyStage;
  market: JourneyMarket;
  persona: JourneyPersona;
  evaluation?: JourneyEvaluation;
  current_step_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  reset_count?: number;
  is_customized?: boolean;
  customization_level?: CustomizationLevel;
}

export interface JourneyStepRow {
  id: string;
  journey_id: string;
  step_index: number;
  title: string;
  title_zh?: string;
  description: string;
  description_zh?: string;
  actions: StepAction[];
  dod_criteria: string[];
  time_estimate_weeks: number;
  status: StepStatus;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  is_customized?: boolean;
  original_step_index?: number;
  skip_justification?: string;
}

export interface WorkflowTemplateRow {
  id: string;
  type: ActionType;
  title: string;
  conditions: string[];
  content: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StepCompletionRow {
  id: string;
  step_id: string;
  user_id: string;
  completion_method: CompletionMethod;
  reflection_notes?: string;
  points_awarded: number;
  created_at: string;
}

export interface JourneyHistoryRow {
  id: string;
  user_id: string;
  original_journey_id: string;
  action_type: JourneyActionType;
  previous_data: any;
  new_data?: any;
  reason?: string;
  created_at: string;
}

export interface StepSkipJustificationRow {
  id: string;
  step_id: string;
  user_id: string;
  justification: string;
  skip_type: SkipType;
  created_at: string;
}

export interface JourneyCustomizationRow {
  id: string;
  journey_id: string;
  user_id: string;
  customization_type: CustomizationType;
  original_value: any;
  custom_value: any;
  applied_at: string;
  is_active: boolean;
}

// API request/response types
export interface CreateJourneyRequest {
  setup: JourneySetup;
}

export interface CreateJourneyResponse {
  journey: Journey;
  steps: JourneyStep[];
}

export interface UpdateStepStatusRequest {
  stepId: string;
  status: StepStatus;
  reflectionNotes?: string;
}

export interface ResetJourneyRequest {
  journeyId: string;
  newSetup?: JourneySetup;
  reason?: string;              // User's reason for resetting
  preserveCustomizations?: boolean; // Whether to preserve customizations
}

export interface SkipStepRequest {
  stepId: string;
  justification: string;
  skipType: SkipType;
}

export interface CustomizeJourneyRequest {
  journeyId: string;
  customizations: JourneyCustomization[];
}

export interface JourneyHistoryEntry {
  id: string;
  originalJourneyId: string;
  actionType: JourneyActionType;
  previousData: any;
  newData?: any;
  reason?: string;
  createdAt: Date;
}

export interface StepSkipJustification {
  id: string;
  stepId: string;
  userId: string;
  justification: string;
  skipType: SkipType;
  createdAt: Date;
}

export interface JourneyCustomization {
  id: string;
  journeyId: string;
  userId: string;
  customizationType: CustomizationType;
  originalValue: any;
  customValue: any;
  appliedAt: Date;
  isActive: boolean;
}

// Utility types for persona detection
export interface PersonaDetectionContext {
  stage: JourneyStage;
  market: JourneyMarket;
  evaluation?: JourneyEvaluation;
  hasSpecificIdea?: boolean;
  hasAudience?: boolean;
  serviceBusiness?: boolean;
}

// Template matching context
export interface TemplateMatchingContext {
  stage: JourneyStage;
  market: JourneyMarket;
  persona: JourneyPersona;
  actionType: ActionType;
  step?: string;                // Current step identifier (e.g., 'idea-generation', 'validation')
  stepIndex?: number;           // Current step index in journey
  aiContext?: AITemplateContext; // Additional AI context for template generation
}

// AI context for template generation
export interface AITemplateContext {
  selectedIdea?: BusinessIdea;
  userSkills?: string[];
  userInterests?: string[];
  validationResults?: ValidationResults;
}

// Business idea interface for AI-generated content
export interface BusinessIdea {
  title: string;
  problem: string;
  solution: string;
  targetCustomer: string;
  validationQuestion: string;
  technicalFeasibility: 'High' | 'Medium' | 'Low';
  estimatedDevelopment: string;
  skillsRequired?: string[];
}

// Validation results interface
export interface ValidationResults {
  problemFrequency: number;
  trialLikelihood: number;
  pricingSignal: string;
  meetsSuccessCriteria: boolean;
}

// Utility functions for converting between database rows and TypeScript interfaces
export function journeyRowToJourney(row: JourneyRow): Journey {
  return {
    id: row.id,
    userId: row.user_id,
    stage: row.stage,
    market: row.market,
    persona: row.persona,
    evaluation: row.evaluation,
    currentStepIndex: row.current_step_index,
    isActive: row.is_active,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    resetCount: row.reset_count,
    isCustomized: row.is_customized,
    customizationLevel: row.customization_level
  };
}

export function journeyStepRowToJourneyStep(row: JourneyStepRow): JourneyStep {
  return {
    id: row.id,
    journeyId: row.journey_id,
    stepIndex: row.step_index,
    title: row.title,
    title_zh: row.title_zh,
    description: row.description,
    description_zh: row.description_zh,
    actions: row.actions,
    dodCriteria: row.dod_criteria,
    timeEstimateWeeks: row.time_estimate_weeks,
    status: row.status,
    completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    isCustomized: row.is_customized,
    originalStepIndex: row.original_step_index,
    skipJustification: row.skip_justification
  };
}

export function workflowTemplateRowToWorkflowTemplate(row: WorkflowTemplateRow): WorkflowTemplate {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    conditions: row.conditions,
    content: row.content,
    isActive: row.is_active,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}

export function stepCompletionRowToStepCompletion(row: StepCompletionRow): StepCompletion {
  return {
    id: row.id,
    stepId: row.step_id,
    userId: row.user_id,
    completionMethod: row.completion_method,
    reflectionNotes: row.reflection_notes,
    pointsAwarded: row.points_awarded,
    createdAt: new Date(row.created_at)
  };
}

export function journeyHistoryRowToJourneyHistoryEntry(row: JourneyHistoryRow): JourneyHistoryEntry {
  return {
    id: row.id,
    originalJourneyId: row.original_journey_id,
    actionType: row.action_type,
    previousData: row.previous_data,
    newData: row.new_data,
    reason: row.reason,
    createdAt: new Date(row.created_at)
  };
}

export function stepSkipJustificationRowToStepSkipJustification(row: StepSkipJustificationRow): StepSkipJustification {
  return {
    id: row.id,
    stepId: row.step_id,
    userId: row.user_id,
    justification: row.justification,
    skipType: row.skip_type,
    createdAt: new Date(row.created_at)
  };
}

export function journeyCustomizationRowToJourneyCustomization(row: JourneyCustomizationRow): JourneyCustomization {
  return {
    id: row.id,
    journeyId: row.journey_id,
    userId: row.user_id,
    customizationType: row.customization_type,
    originalValue: row.original_value,
    customValue: row.custom_value,
    appliedAt: new Date(row.applied_at),
    isActive: row.is_active
  };
}
